import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { Account } from "@/api/types";
import { useApi } from "@/context/ApiContext";
import { Card } from "@/components/ui/kit";
import { ConnectPanel } from "@/components/exchange/Connect";
import { useProviderInfos } from "@/hooks/useProviderInfos";
import { accountProviderTradeTypes, buildMarketplacePickerCards } from "@/lib/account-provider";

/** Change the API keys (and name) of an existing exchange connection. */
export default function ExchangeEdit() {
  const { id = "" } = useParams();
  const api = useApi(["accounts"]);
  const navigate = useNavigate();
  const providerInfos = useProviderInfos();
  const account: Account | undefined = api.accounts.find((a: Account) => a.id === id);

  const card = useMemo(() => {
    if (!account) return null;
    const network = String(account.config?.network || "");
    const cards = buildMarketplacePickerCards([account.provider]);
    return cards.find((c) => !c.staticOptions.network || c.staticOptions.network === network) || cards[0] || null;
  }, [account]);

  if (!api.ready) return <Card className="py-8 text-center text-sm text-[var(--text-muted)]">Loading…</Card>;
  if (!account || !card) return <Card className="py-8 text-center text-sm text-[var(--text-muted)]">Account not found.</Card>;

  const types = accountProviderTradeTypes(
    providerInfos?.account_providers.find((i) => i.id === card.provider),
    card.staticOptions
  );

  return (
    <div className="flex min-w-0 max-w-[720px] flex-col gap-5">
      <div>
        <h1 className="m-0 text-[22px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">Change keys</h1>
        <p className="m-0 mt-1 text-[13px] text-[var(--text-muted)]">
          New keys replace the saved ones. Live strategies on this exchange reconnect with them.
        </p>
      </div>
      <ConnectPanel
        card={card}
        types={types}
        account={account}
        onConnected={() => navigate(`/integrations/marketplaces/${account.id}`)}
      />
    </div>
  );
}
